import type { UseTeamDataResult } from '../../controller/queries/useShareDialogTeamDataHandler.ts';
import type {
	GeneralAccessLevel,
	GroupOrTeamPrincipal,
	KnownErrors,
	Principal,
	PrincipalAndRole,
	Role,
} from '../../controlled/commonTypes.tsx';
import type { ContentRestrictionsQueryData } from '../Controller.tsx';

import type {
	ConfluenceContentRestrictionState,
	useContentPermissionsQuery$data as ContentPermissionsData,
} from './__generated__/useContentPermissionsQuery.graphql.ts';
import type {
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild_nodes_Content_restrictions_ContentRestrictions as ContentRestrictions,
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild_nodes_Content_restrictions_ContentRestrictions_read_ContentRestriction as ReadContentRestriction,
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild_nodes_Content_restrictions_ContentRestrictions_update_ContentRestriction as UpdateContentRestriction,
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild_nodes_Content_restrictions_ContentRestrictions_update_ContentRestriction_restrictions_SubjectsByType_group_PaginatedGroupList as GroupsWithUpdateRestrictions,
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild_nodes_Content_restrictions_ContentRestrictions_update_ContentRestriction_restrictions_SubjectsByType_userWithRestrictions_PaginatedUserWithRestrictions as UsersWithUpdateRestrictions,
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild_nodes_Content_restrictions_ContentRestrictions_read_ContentRestriction_restrictions_SubjectsByType_group_PaginatedGroupList as GroupsWithReadRestrictions,
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild_nodes_Content_restrictions_ContentRestrictions_read_ContentRestriction_restrictions_SubjectsByType_userWithRestrictions_PaginatedUserWithRestrictions as UsersWithReadRestrictions,
} from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import { ConfluenceGroupUsageType } from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import type { GroupAndTeamCountsMapType } from './useGroupsCountQuery.ts';

type QueryNodes = NonNullable<
	ContentPermissionsData['confluence_contentPermissions']
>['confluencePrincipalsConnection']['nodes'];

type UsersAndGroups = {
	users: Principal[];
	groups: GroupOrTeamPrincipal[];
};

export type ReadAndUpdate = {
	read: UsersAndGroups;
	update: UsersAndGroups;
};

type RestrictionsSource = ContentRestrictions & {
	groupAndTeamCountsMap: GroupAndTeamCountsMapType;
	teamData: UseTeamDataResult['teamData'];
};

/**
 * Converts the raw query data to the internal representation for content restrictions
 */
export function getContentRestrictions(
	restrictions: RestrictionsSource,
	currentUser: Principal,
):
	| (ContentRestrictionsQueryData & {
			groupCount: number;
			teamCount: number;
			userCount: number;
			currentUserHasAccess: boolean;
	  })
	| null {
	const readAndUpdateLists = convertRestrictionsObject(restrictions);

	const { groupCount, teamCount, userCount, principals, currentUserHasAccess } =
		getPrincipalsAndRoles(readAndUpdateLists, currentUser);
	return {
		generalAccessLevel: getGeneralAccessLevel(readAndUpdateLists),
		principals,
		groupCount,
		teamCount,
		userCount,
		currentUserHasAccess,
	};
}

/**
 * Converts the new "content permissions" query node list into the legacy shape expected by
 * downstream restriction helpers.
 *
 * The legacy helpers (e.g. `getPrincipalsAndRoles`) expect principals bucketed into `read` and
 * `update` lists. The new query returns a single node list with explicit/effective permissions.
 */
export function convertQueryNodes({
	nodes,
	groupAndTeamCountsMap,
	teamData,
}: {
	nodes: QueryNodes;
	groupAndTeamCountsMap: GroupAndTeamCountsMapType;
	teamData: UseTeamDataResult['teamData'];
}): ReadAndUpdate {
	const read: UsersAndGroups = { users: [], groups: [] };
	const update: UsersAndGroups = { users: [], groups: [] };

	for (const node of nodes) {
		if (!node) continue;
		// Exclude “share” restrictions from permission checks.
		// These are draft-only restrictions created when a draft is shared, which get a shareId.
		// They aren’t treated as real permissions because they disappear once the page is published.
		if (node.explicitPermission === 'SHARE') continue;

		const destination = node.explicitPermission === 'EDIT' ? update : read;

		if (node.principalType === 'USER') {
			destination.users.push({
				type: 'user',
				id: node.principalId,
				name: node.displayName,
				avatarUrl: node.avatar ?? '',
				email: '',
				knownErrors: getKnownErrors({
					hasSpaceEditPermission: node.hasSpaceEditPermission,
					hasSpaceViewPermission: node.hasSpaceViewPermission,
					restrictingContent: node.restrictingContent,
				}),
				sitePermissionType: node.sitePermissionType ?? '',
			});
			continue;
		}

		if (node.principalType === 'GROUP') {
			const id = node.principalId;
			const isTeam = node.usageType === 'TEAM_COLLABORATION';
			// Find matching team in teamsV2 array - IDs may be in ARI format
			const teamItem = isTeam
				? teamData?.teamsV2?.find((team) => {
						const teamIdSegment =
							team?.id && team?.id?.toString().lastIndexOf('/') >= 0
								? team?.id?.toString().substring(team?.id?.toString().lastIndexOf('/') + 1)
								: team?.id;
						return teamIdSegment === id;
					})
				: undefined;

			// Match legacy behavior: treat null/unknown usageType as USERBASE_GROUP.
			const usageType =
				node.usageType === 'TEAM_COLLABORATION'
					? ConfluenceGroupUsageType.TEAM_COLLABORATION
					: ConfluenceGroupUsageType.USERBASE_GROUP;

			destination.groups.push({
				type: 'group',
				id,
				name: node.displayName,
				usageType,
				memberCount: groupAndTeamCountsMap.get(id) ?? 0,
				isVerified: teamItem?.type?.verified ?? false,
				avatarSrc: teamItem?.largeAvatarImageUrl ?? '',
				teamTypeName: teamItem?.type?.name,
			});
		}
	}

	return { read, update };
}

export function mapQueryModeToGeneralAccessLevel(
	mode: ConfluenceContentRestrictionState | null | undefined,
): GeneralAccessLevel {
	if (mode === 'VIEW_RESTRICTED') return 'restricted';
	if (mode === 'EDIT_RESTRICTED') return 'open-can-view';
	return 'open-can-edit';
}

/**
 * Converts the 'read' and 'update' structures from the query data into their
 * structural equivalents but using our own Principal
 */
function convertRestrictionsObject(source: RestrictionsSource): ReadAndUpdate {
	const ret: ReadAndUpdate = {
		read: { users: [], groups: [] },
		update: { users: [], groups: [] },
	};

	const { read, update, groupAndTeamCountsMap, teamData } = source;

	if (read) convertUsersAndGroups(read, ret.read, groupAndTeamCountsMap, teamData);
	if (update) convertUsersAndGroups(update, ret.update, groupAndTeamCountsMap, teamData);

	return ret;
}

/**
 * Iterates through the users and groups nodes of query data, converting
 * entries into Principal format, and pushing them to accumulators
 */
function convertUsersAndGroups(
	source: ReadContentRestriction | UpdateContentRestriction,
	{ users, groups }: UsersAndGroups,
	groupAndTeamCountsMap: GroupAndTeamCountsMapType,
	teamData: UseTeamDataResult['teamData'],
) {
	const { restrictions } = source;
	if (restrictions) {
		const { userWithRestrictions, group } = restrictions;

		if (userWithRestrictions) pushUserNodes(userWithRestrictions, users);
		if (group) pushGroupNodes(group, groups, groupAndTeamCountsMap, teamData);
	}
}

/**
 * Determines the General Access level, based on are there any listed viewers,
 * and are there any listed editors
 */
export function getGeneralAccessLevel({
	read: { groups: readGroups, users: readUsers },
	update: { groups: updateGroups, users: updateUsers },
}: ReadAndUpdate): GeneralAccessLevel {
	const hasViewPrincipals = readGroups.length + readUsers.length > 0;
	const hasEditPrincipals = updateGroups.length + updateUsers.length > 0;

	if (hasViewPrincipals) return 'restricted';
	if (hasEditPrincipals) return 'open-can-view';
	return 'open-can-edit';
}

/**
 * Coalesce the read and update lists into a single, sorted list, with each principal
 * appearing only once, alongside their intended role
 */
export function getPrincipalsAndRoles(
	{ read, update }: ReadAndUpdate,
	currentUser: Principal,
): {
	principals: PrincipalAndRole[];
	currentUserHasAccess: boolean;
	groupCount: number;
	teamCount: number;
	userCount: number;
} {
	const map = new Map<string, PrincipalAndRole>();

	const addToMapWithRole = (principals: Principal[], role: Role) =>
		principals.forEach((principal) => map.set(principal.id, { role, principal }));

	// Initially, populate the map with viewers
	addToMapWithRole([...read.users, ...read.groups], 'can-view');

	// Do another pass for the editors, potentially overwriting entries set in the
	// first pass, with their edit role
	addToMapWithRole([...update.users, ...update.groups], 'can-edit');

	// Convert the map to a PrincipalAndRole array, and sort by name
	const unsortedArray = Array.from(map).map(([, principalAndRole]) => principalAndRole);
	const { groupCount, teamCount, userCount } = unsortedArray.reduce(
		(counts, { principal }) => {
			if (principal.type === 'group') {
				if (principal.usageType === ConfluenceGroupUsageType.USERBASE_GROUP) {
					counts.groupCount++;
				} else if (principal.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION) {
					counts.teamCount++;
				} else if (principal.usageType === null) {
					// When usageType is null (because the BE sends the data but teams are not yet supported on the FE),
					// fall back to default user base group behavior
					counts.groupCount++;
				}
			}

			if (principal.type === 'user') {
				counts.userCount++;
			}

			return counts;
		},
		{ groupCount: 0, teamCount: 0, userCount: 0 },
	);

	const currentUserHasAccess = unsortedArray.some(
		({ principal }) => principal.id === currentUser.id,
	);
	return {
		principals: Array.from(map)
			.map(([, principalAndRole]) => principalAndRole)
			.sort(
				(
					{ principal: { name: a } }: PrincipalAndRole,
					{ principal: { name: b } }: PrincipalAndRole,
				) => {
					if (a === b) return 0;
					else if (a > b) return 1;
					return -1;
				},
			),
		currentUserHasAccess,
		groupCount,
		teamCount,
		userCount,
	};
}

/**
 * Converts groups from query format to Principal format, and pushes them to an accumulator
 */
function pushGroupNodes(
	source: GroupsWithUpdateRestrictions | GroupsWithReadRestrictions,
	dest: GroupOrTeamPrincipal[],
	groupAndTeamCountsMap: GroupAndTeamCountsMapType,
	teamData: UseTeamDataResult['teamData'],
) {
	const { nodes } = source;
	if (nodes) {
		for (const node of nodes) {
			if (!node) continue;
			const id = node?.id || '';
			// Find matching team in teamsV2 array - IDs may be in ARI format
			const nodeIdSegment =
				id.toString().lastIndexOf('/') >= 0
					? id.toString().substring(id.toString().lastIndexOf('/') + 1)
					: id;
			const teamItem =
				node?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION
					? teamData?.teamsV2?.find((team) => {
							const teamIdSegment =
								team?.id && team?.id?.toString().lastIndexOf('/') >= 0
									? team?.id?.toString().substring(team?.id?.toString().lastIndexOf('/') + 1)
									: team?.id;
							return teamIdSegment === nodeIdSegment;
						})
					: undefined;
			dest.push({
				type: 'group',
				id: node.id || '',
				name: node.name || '',
				usageType: node.usageType,
				memberCount: groupAndTeamCountsMap.get(node.id || '') ?? 0,
				isVerified: teamItem?.type?.verified ?? false,
				avatarSrc: teamItem?.largeAvatarImageUrl ?? '',
				teamTypeName: teamItem?.type?.name,
			});
		}
	}
}

/**
 * Converts users from query format to Principal format, and pushes them to an accumulator
 */
function pushUserNodes(
	source: UsersWithUpdateRestrictions | UsersWithReadRestrictions,
	dest: Principal[],
) {
	const { nodes } = source;
	if (nodes) {
		for (const node of nodes) {
			if (!node) continue;

			/**
			 * TODO: there are some fields we were querying for in the Restrictions
			 * dialog, but not using here (yet):
			 * • type
			 */

			dest.push({
				type: 'user',
				id: node.accountId || '',
				name: node.displayName || '',
				avatarUrl: node.profilePicture?.path || '', // TODO
				email: node.email || '',
				knownErrors: getKnownErrors(node),
				sitePermissionType: node.permissionType || '',
			});
		}
	}
}

/**
 * Retrieves known errors based on the given principal's restriction metadata.
 *
 * This is used by both:
 * - legacy restrictions query user nodes
 * - content-permissions query user nodes
 */
type KnownErrorsInput = {
	hasSpaceEditPermission?: boolean | null;
	hasSpaceViewPermission?: boolean | null;
	restrictingContent?: {
		links?: {
			webui?: string | null;
			base?: string | null;
		} | null;
	} | null;
};

export function getKnownErrors(node: KnownErrorsInput): KnownErrors {
	const { restrictingContent, hasSpaceEditPermission, hasSpaceViewPermission } = node;

	if (hasSpaceViewPermission === false) {
		return { accessWarning: 'space-view-access-warning', parentLink: null };
	}
	if (restrictingContent) {
		return {
			accessWarning: 'parent-access-warning',
			parentLink: {
				webui: restrictingContent.links?.webui,
				base: restrictingContent.links?.base,
			},
		};
	}
	// TODO: Implement a check for the principal's current access level.
	// If the access level is 'view', it's not relevant.
	// If the access level is 'edit', it is relevant.
	if (hasSpaceEditPermission === false) {
		return { accessWarning: 'space-edit-access-warning', parentLink: null };
	}
	return undefined;
}
